/**
 * Extracts the 11-character YouTube video ID from any common input:
 * - Full watch URLs (https://www.youtube.com/watch?v=ID)
 * - Short URLs (https://youtu.be/ID)
 * - Shorts URLs (https://youtube.com/shorts/ID)
 * - Embed URLs (https://www.youtube.com/embed/ID)
 * - Bare 11-character IDs
 */
export const extractYouTubeVideoId = (input: string): string | null => {
  const raw = input.trim();
  if (!raw) return null;

  const idLike = /^[a-zA-Z0-9_-]{11}$/;
  if (idLike.test(raw)) return raw;

  try {
    const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return idLike.test(id ?? "") ? (id as string) : null;
    }

    if (host.endsWith("youtube.com")) {
      const v = url.searchParams.get("v");
      if (v && idLike.test(v)) return v;

      const parts = url.pathname.split("/").filter(Boolean);
      if ((parts[0] === "shorts" || parts[0] === "embed") && idLike.test(parts[1] ?? "")) {
        return parts[1]!;
      }

      const last = parts[parts.length - 1];
      if (idLike.test(last ?? "")) return last!;
    }
  } catch {
    // ignore
  }

  const match = raw.match(/([a-zA-Z0-9_-]{11})/);
  return match && idLike.test(match[1]) ? match[1] : null;
};
